/**
 * Organization Dashboard — vehicles, members, earnings overview.
 *
 * Uses ScreenContainer for responsive web/mobile layout.
 * Stats grid, vehicle list, quick actions.
 */
import { useCallback, useState } from 'react'
import { View, Text, RefreshControl } from 'react-native'
import { ScrollView } from 'react-native-gesture-handler'
import { useRouter, useLocalSearchParams } from 'expo-router'
import { useTranslation } from 'react-i18next'
import { notification, NotificationType } from '@/lib/utils/haptics'
import Animated from 'react-native-reanimated'
import { Image as ExpoImage } from 'expo-image'
import { toPlaceholder, GENERIC_IMAGE_BLURHASH } from '@/lib/utils/blurhash'
import {
  Building2,
  Car,
  Users,
  DollarSign,
  Settings,
  UserPlus,
  ShieldCheck,
  ChevronRight,
  Wallet,
} from '@/components/ui/icons'
import { Pressable } from '@/components/ui/Pressable'
import { Button } from '@/components/ui/Button'
import { Badge } from '@/components/ui/Badge'
import { EmptyState } from '@/components/ui/EmptyState'
import { Skeleton } from '@/components/ui/Skeleton'
import { ScreenHeader } from '@/components/ui/ScreenHeader'
import { ScreenContainer } from '@/components/ui/ScreenContainer'
import { ScreenState } from '@/components/ui/ScreenState'
import {
  useOrganization,
  useOrgMembers,
  useClaimableVehicles,
  useClaimVehicle,
} from '@/lib/hooks/use-organization'
import { useOrgEarnings } from '@/lib/hooks/use-payouts'
import { useAuth } from '@/lib/hooks/use-auth'
import { useSemanticColors, SPACING, RADIUS } from '@/lib/theme/tokens'
import { COLORS, FONTS } from '@/lib/brand'
import { WEB_SHADOWS } from '@/lib/theme/web-shadow'
import { useTheme } from '@/lib/hooks/use-theme'
import { useEntrance } from '@/lib/hooks/use-entrance'
import { toast } from '@/lib/toast'
import { captureError, errorMessage } from '@/lib/utils/observability'
import { formatCurrency } from '@/lib/utils/format'

/** Org logo tile, shared with the skeleton that stands in for it. */
const ORG_LOGO_SIZE = 56

/**
 * Rendered height of one StatCard: icon (22) + value (20) + label (16) plus
 * two SPACING.xs gaps and SPACING.md padding top and bottom. Hardcoding the
 * sum keeps the skeleton the same height as the card without measuring.
 */
const ORG_STAT_CARD_HEIGHT = 94

function StatCard({
  icon: Icon,
  label,
  value,
  colors,
}: {
  icon: typeof Car
  label: string
  value: string | number
  colors: ReturnType<typeof useSemanticColors>
}) {
  return (
    <View
      style={{
        flex: 1,
        // Pinned so the loading skeleton can be the same height as the card.
        // Without it the placeholder is a guess and the row below jumps.
        minHeight: ORG_STAT_CARD_HEIGHT,
        backgroundColor: colors.surface,
        borderRadius: RADIUS.lg,
        padding: SPACING.md,
        alignItems: 'center',
        gap: SPACING.xs,
        ...WEB_SHADOWS.sm,
      }}
    >
      <Icon size={22} color={COLORS.coral.DEFAULT} strokeWidth={1.8} />
      <Text style={{ fontSize: 20, fontFamily: FONTS.bodyBold, color: colors.textPrimary }}>
        {value}
      </Text>
      <Text
        style={{
          fontSize: 12,
          fontFamily: FONTS.body,
          color: colors.textSecondary,
          textAlign: 'center',
        }}
      >
        {label}
      </Text>
    </View>
  )
}

export default function OrgDashboardScreen() {
  const { t } = useTranslation()
  const router = useRouter()
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  const { orgId } = useLocalSearchParams<{ orgId: string }>()

  const { user } = useAuth()
  const { organization, isLoading, isRefetching, isError, refetch } = useOrganization(orgId)
  const enter = useEntrance(!isLoading)
  const { members } = useOrgMembers(orgId)
  const { claimableVehicles } = useClaimableVehicles(orgId)
  const claimVehicle = useClaimVehicle()
  const [claimingVehicleId, setClaimingVehicleId] = useState<string | null>(null)

  // Only the fleet_manager (legal rep) sees the payout CTA. We gate at the
  // UI layer AND the RLS layer — hiding the CTA avoids the dead-link
  // experience if a driver somehow lands here.
  const isFleetManager = members.some(
    (m) =>
      m.profileId === user?.id && m.role === 'fleet_manager' && m.isActive && Boolean(m.acceptedAt),
  )
  void user // keep the reference for the members check

  const { data: earnings } = useOrgEarnings(isFleetManager ? orgId : undefined)
  const handleClaimVehicle = useCallback(
    async (vehicleId: string) => {
      if (!orgId) return
      setClaimingVehicleId(vehicleId)
      try {
        await claimVehicle.mutateAsync({ orgId, vehicleId })
        void notification(NotificationType.Success)
        toast.success(t('org.vehicle_claimed'))
      } catch (err) {
        void notification(NotificationType.Error)
        toast.error(errorMessage(err, t('errors.generic')))
        captureError(
          err,
          { screen: 'org_dashboard', action: 'claim_vehicle' },
          { orgId, vehicleId },
        )
      } finally {
        setClaimingVehicleId(null)
      }
    },
    [claimVehicle, orgId, t],
  )

  // One container, one JSX position: three sibling returns each re-wrapped
  // their own copy. ScreenState owns the region that varies — and a failed
  // org fetch now offers a retry, where the old `!organization` card was a
  // dead end with nothing to tap.
  return (
    <ScreenContainer variant="content" scroll={false}>
      {orgId ? (
        <ScreenState
          query={{ data: organization, isLoading, isError, refetch }}
          skeleton={
            <>
              <ScreenHeader backTestID="org.dashboard-back" />
              {/* The dashboard opens with a 56pt org logo beside its name and
                  then THREE stat cards. The old skeleton drew a title bar and
                  two half-width cards, so the header row and the third stat
                  both appeared out of nowhere. */}
              <View style={{ padding: SPACING.lg }}>
                <View
                  style={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: SPACING.md,
                    marginTop: SPACING.md,
                    marginBottom: SPACING.lg,
                  }}
                >
                  <Skeleton width={ORG_LOGO_SIZE} height={ORG_LOGO_SIZE} borderRadius={RADIUS.lg} />
                  <Skeleton width="55%" height={24} borderRadius={RADIUS.sm} />
                </View>
                <View style={{ flexDirection: 'row', gap: SPACING.sm }}>
                  {[0, 1, 2].map((card) => (
                    <View key={card} style={{ flex: 1 }}>
                      <Skeleton
                        width="100%"
                        height={ORG_STAT_CARD_HEIGHT}
                        borderRadius={RADIUS.lg}
                        staggerIndex={card}
                      />
                    </View>
                  ))}
                </View>
              </View>
            </>
          }
        >
          {(organization) => (
            <ScrollView
              showsVerticalScrollIndicator={false}
              refreshControl={
                <RefreshControl
                  refreshing={isRefetching}
                  onRefresh={() => void refetch()}
                  tintColor={COLORS.coral.DEFAULT}
                />
              }
              contentContainerStyle={{
                padding: SPACING.lg,
                paddingBottom: SPACING['3xl'] + SPACING['2xl'],
              }}
            >
              <ScreenHeader
                backTestID="org.dashboard-back"
                style={{ paddingHorizontal: 0, paddingTop: 0, paddingBottom: SPACING.md }}
              />

              {/* Header */}
              <Animated.View
                collapsable={false}
                entering={enter(1)}
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: SPACING.md,
                  marginTop: SPACING.md,
                  marginBottom: SPACING.lg,
                }}
              >
                <View
                  style={{
                    width: ORG_LOGO_SIZE,
                    height: ORG_LOGO_SIZE,
                    borderRadius: RADIUS.lg,
                    backgroundColor: colors.surfaceSubtle,
                    borderWidth: 1,
                    borderColor: colors.border,
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden',
                  }}
                >
                  {organization.logoUrl ? (
                    <ExpoImage
                      source={organization.logoUrl}
                      placeholder={toPlaceholder(GENERIC_IMAGE_BLURHASH)}
                      style={{ width: 56, height: 56 }}
                      contentFit="cover"
                    />
                  ) : (
                    <Building2 size={28} color={COLORS.coral.DEFAULT} strokeWidth={1.5} />
                  )}
                </View>
                <View style={{ flex: 1 }}>
                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: SPACING.xs }}>
                    <Text
                      style={{
                        fontSize: 22,
                        fontFamily: FONTS.bodyBold,
                        color: colors.textPrimary,
                        flexShrink: 1,
                        letterSpacing: -0.3,
                      }}
                      numberOfLines={1}
                    >
                      {organization.name}
                    </Text>
                    {organization.isVerified && (
                      <ShieldCheck size={20} color={colors.success} strokeWidth={2} />
                    )}
                  </View>
                  <Text
                    style={{
                      fontSize: 13,
                      fontFamily: FONTS.body,
                      color: colors.textSecondary,
                      marginTop: 2,
                    }}
                    numberOfLines={1}
                  >
                    {organization.legalName}
                  </Text>
                </View>
              </Animated.View>

              {/* Claimable vehicles */}
              {isFleetManager && claimableVehicles.length > 0 && (
                <Animated.View collapsable={false} entering={enter(2)}>
                  <View
                    testID="org.claimable-vehicles"
                    style={{
                      padding: SPACING.md,
                      borderRadius: RADIUS.lg,
                      backgroundColor: colors.surface,
                      borderWidth: 1,
                      borderColor: colors.border,
                      gap: SPACING.sm,
                      marginBottom: SPACING.lg,
                      ...WEB_SHADOWS.sm,
                    }}
                  >
                    <View
                      style={{
                        flexDirection: 'row',
                        alignItems: 'center',
                        gap: SPACING.sm,
                      }}
                    >
                      <View
                        style={{
                          width: 36,
                          height: 36,
                          borderRadius: RADIUS.md,
                          backgroundColor: `${COLORS.amber.DEFAULT}14`,
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <Car size={18} color={COLORS.amber.DEFAULT} strokeWidth={1.8} />
                      </View>
                      <View style={{ flex: 1, minWidth: 0 }}>
                        <Text
                          style={{
                            fontSize: 15,
                            fontFamily: FONTS.bodyBold,
                            color: colors.textPrimary,
                          }}
                        >
                          {t('org.claim_vehicles')}
                        </Text>
                        <Text
                          style={{
                            fontSize: 12,
                            lineHeight: 18,
                            fontFamily: FONTS.body,
                            color: colors.textSecondary,
                            marginTop: 2,
                          }}
                        >
                          {t('org.claim_desc', { count: claimableVehicles.length })}
                        </Text>
                      </View>
                    </View>
                    {claimableVehicles.map((vehicle) => (
                      <View
                        key={vehicle.id}
                        style={{
                          flexDirection: 'row',
                          alignItems: 'center',
                          gap: SPACING.sm,
                          paddingTop: SPACING.sm,
                          borderTopWidth: 1,
                          borderTopColor: colors.border,
                        }}
                      >
                        <View style={{ flex: 1, minWidth: 0 }}>
                          <Text
                            style={{
                              fontSize: 14,
                              fontFamily: FONTS.bodyBold,
                              color: colors.textPrimary,
                            }}
                            numberOfLines={1}
                          >
                            {vehicle.title}
                          </Text>
                          <Text
                            style={{
                              fontSize: 12,
                              lineHeight: 18,
                              fontFamily: FONTS.body,
                              color: colors.textMuted,
                            }}
                            numberOfLines={1}
                          >
                            {vehicle.detectedLegalName}
                          </Text>
                        </View>
                        <Button
                          title={t('org.claim_vehicle_action')}
                          onPress={() => void handleClaimVehicle(vehicle.id)}
                          loading={claimingVehicleId === vehicle.id}
                          disabled={claimVehicle.isPending}
                          variant="secondary"
                          size="sm"
                          testID={`org.claim-vehicle-${vehicle.id}`}
                        />
                      </View>
                    ))}
                  </View>
                </Animated.View>
              )}

              {/* Stats */}
              <Animated.View
                collapsable={false}
                entering={enter(3)}
                style={{ flexDirection: 'row', gap: SPACING.sm, marginBottom: SPACING.lg }}
              >
                <StatCard
                  icon={Car}
                  label={t('org.vehicles')}
                  value={organization.vehicleCount ?? 0}
                  colors={colors}
                />
                <StatCard
                  icon={Users}
                  label={t('org.members')}
                  value={members.length}
                  colors={colors}
                />
                <StatCard
                  icon={DollarSign}
                  label={t('org.earnings')}
                  value={earnings ? formatCurrency(earnings.ytdCents) : '—'}
                  colors={colors}
                />
              </Animated.View>

              {/* Quick Actions */}
              <Animated.View collapsable={false} entering={enter(4)} style={{ gap: SPACING.sm }}>
                <Text
                  style={{
                    fontSize: 12,
                    color: colors.textSecondary,
                    textTransform: 'uppercase',
                    letterSpacing: 0.8,
                    fontFamily: FONTS.body,
                    marginBottom: SPACING.xs,
                  }}
                >
                  {t('common.actions')}
                </Text>

                {/* Members */}
                <Pressable
                  onPress={() =>
                    router.push(`/hosting/organization/members?orgId=${orgId}` as never)
                  }
                  accessibilityRole="button"
                  accessibilityLabel={t('org.members')}
                  testID="org.members-action"
                >
                  <View
                    style={{
                      flexDirection: 'row',
                      alignItems: 'center',
                      backgroundColor: colors.surface,
                      borderRadius: RADIUS.md,
                      padding: SPACING.md,
                      gap: SPACING.md,
                      ...WEB_SHADOWS.sm,
                    }}
                  >
                    <View
                      style={{
                        width: 44,
                        height: 44,
                        borderRadius: RADIUS.md,
                        backgroundColor: colors.surfaceSubtle,
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <UserPlus size={22} color={colors.textSecondary} strokeWidth={1.5} />
                    </View>
                    <View style={{ flex: 1 }}>
                      <Text
                        style={{
                          fontSize: 15,
                          fontFamily: FONTS.bodyBold,
                          color: colors.textPrimary,
                        }}
                      >
                        {t('org.members')}
                      </Text>
                      <Text
                        style={{
                          fontSize: 13,
                          fontFamily: FONTS.body,
                          color: colors.textSecondary,
                          marginTop: 2,
                        }}
                      >
                        {t('org.invite_member')}
                      </Text>
                    </View>
                    <ChevronRight size={18} color={colors.textMuted} strokeWidth={1.5} />
                  </View>
                </Pressable>

                {/* Payouts — only visible to fleet_manager */}
                {isFleetManager && (
                  <Pressable
                    onPress={() =>
                      router.push(`/hosting/organization/payouts?orgId=${orgId}` as never)
                    }
                    accessibilityRole="button"
                    accessibilityLabel={t('org_payouts.title')}
                    testID="org.payouts-action"
                  >
                    <View
                      style={{
                        flexDirection: 'row',
                        alignItems: 'center',
                        backgroundColor: colors.surface,
                        borderRadius: RADIUS.md,
                        padding: SPACING.md,
                        gap: SPACING.md,
                        ...WEB_SHADOWS.sm,
                      }}
                    >
                      <View
                        style={{
                          width: 44,
                          height: 44,
                          borderRadius: RADIUS.md,
                          backgroundColor: colors.surfaceSubtle,
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <Wallet size={22} color={colors.textSecondary} strokeWidth={1.5} />
                      </View>
                      <View style={{ flex: 1 }}>
                        <Text
                          style={{
                            fontSize: 15,
                            fontFamily: FONTS.bodyBold,
                            color: colors.textPrimary,
                          }}
                        >
                          {t('org_payouts.title')}
                        </Text>
                        <Text
                          style={{
                            fontSize: 13,
                            fontFamily: FONTS.body,
                            color: colors.textSecondary,
                            marginTop: 2,
                          }}
                          numberOfLines={1}
                        >
                          {t('org_payouts.cta_subtitle')}
                        </Text>
                      </View>
                      <ChevronRight size={18} color={colors.textMuted} strokeWidth={1.5} />
                    </View>
                  </Pressable>
                )}

                {/* Settings */}
                <Pressable
                  onPress={() =>
                    router.push(`/hosting/organization/settings?orgId=${orgId}` as never)
                  }
                  accessibilityRole="button"
                  accessibilityLabel={t('org.settings')}
                  testID="org.settings-action"
                >
                  <View
                    style={{
                      flexDirection: 'row',
                      alignItems: 'center',
                      backgroundColor: colors.surface,
                      borderRadius: RADIUS.md,
                      padding: SPACING.md,
                      gap: SPACING.md,
                      ...WEB_SHADOWS.sm,
                    }}
                  >
                    <View
                      style={{
                        width: 44,
                        height: 44,
                        borderRadius: RADIUS.md,
                        backgroundColor: colors.surfaceSubtle,
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Settings size={22} color={colors.textSecondary} strokeWidth={1.5} />
                    </View>
                    <View style={{ flex: 1 }}>
                      <Text
                        style={{
                          fontSize: 15,
                          fontFamily: FONTS.bodyBold,
                          color: colors.textPrimary,
                        }}
                      >
                        {t('org.settings')}
                      </Text>
                      <Text
                        style={{
                          fontSize: 13,
                          fontFamily: FONTS.body,
                          color: colors.textSecondary,
                          marginTop: 2,
                        }}
                      >
                        {organization.legalName ?? organization.name}
                      </Text>
                    </View>
                    <ChevronRight size={18} color={colors.textMuted} strokeWidth={1.5} />
                  </View>
                </Pressable>

                {/* Verification status */}
                {!organization.isVerified && (
                  <Pressable
                    onPress={() =>
                      router.push(`/hosting/organization/settings?orgId=${orgId}` as never)
                    }
                    accessibilityRole="button"
                    accessibilityLabel={t('org.verification')}
                    testID="org.verification-action"
                  >
                    <View
                      style={{
                        flexDirection: 'row',
                        alignItems: 'center',
                        backgroundColor: colors.surface,
                        borderRadius: RADIUS.md,
                        padding: SPACING.md,
                        gap: SPACING.md,
                        ...WEB_SHADOWS.sm,
                      }}
                    >
                      <View
                        style={{
                          width: 44,
                          height: 44,
                          borderRadius: RADIUS.md,
                          backgroundColor: `${colors.warning}15`,
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <ShieldCheck size={22} color={colors.warning} strokeWidth={1.5} />
                      </View>
                      <View style={{ flex: 1 }}>
                        <Text
                          style={{
                            fontSize: 15,
                            fontFamily: FONTS.bodyBold,
                            color: colors.textPrimary,
                          }}
                        >
                          {t('org.verification')}
                        </Text>
                        <Text
                          style={{
                            fontSize: 13,
                            fontFamily: FONTS.body,
                            color: colors.warning,
                            marginTop: 2,
                          }}
                        >
                          {t('org.verification_pending')}
                        </Text>
                      </View>
                      <Badge label={t('common.pending')} variant="warning" size="sm" />
                    </View>
                  </Pressable>
                )}
              </Animated.View>
            </ScrollView>
          )}
        </ScreenState>
      ) : (
        // No `orgId` means there is no organization to show — reached by a
        // deep link or by hand-editing the URL. "No encontramos lo que
        // buscás." under a naked back arrow named nothing, explained nothing
        // and offered nothing; the state a person is actually in here is "you
        // do not have an organization yet", and the way out of it is to
        // create one.
        //
        // The heading names that state (`org.no_organization`) instead of
        // repeating the button's own label: it used to say "Crear organización"
        // twice, ~300px apart, as the two largest elements on an empty screen.
        // Heading = where you are, button = what you can do.
        <>
          <ScreenHeader title={t('org.title')} backTestID="org.dashboard-back" />
          <View style={{ flex: 1, justifyContent: 'center' }}>
            <EmptyState
              variant="bloom"
              illustration={<Building2 size={36} color={COLORS.coral.DEFAULT} strokeWidth={1.6} />}
              title={t('org.no_organization')}
              description={t('org.create_desc')}
              action={{
                label: t('org.create'),
                onPress: () => router.push('/hosting/organization/setup' as never),
              }}
            />
          </View>
        </>
      )}
    </ScreenContainer>
  )
}
